import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { SOMETHING_WRONG } from '../../../../../../../utils/axios-interceptor';
import { PROVISIONAL_FILE_VIEW_ERROR_TOAST_ID } from './constants';
import { fetchProvisionalFileStatementData } from './apiHelper';
import { ProvisionalFileStatementData, ProvisionalFileStatementDataParams } from './types';

/** One statement of one file, fetched straight from the underwriting engine; idle unless a viewable file is selected. */
export function useProvisionalFileStatementData({ orgId, docId, mis, enabled, nature, refreshKey }: ProvisionalFileStatementDataParams) {
    const active = enabled && !!docId && !!orgId;
    const requestKey = `${orgId}::${docId ?? ''}::${mis}::${nature ?? ''}::${refreshKey ?? 0}`;
    const [held, setHeld] = useState<{ key: string; data: ProvisionalFileStatementData }>({ key: '', data: {} });
    const requestRef = useRef(0);

    const isCurrent = held.key === requestKey;
    const tableData: ProvisionalFileStatementData = active && isCurrent ? held.data : {};
    const loading = active ? !isCurrent : false;

    const fetchTableData = useCallback(async () => {
        if (!active || !docId) return;
        const requestId = ++requestRef.current;
        const result = await fetchProvisionalFileStatementData({ orgId, docId, mis, nature });
        if (!result.success) toast.error(SOMETHING_WRONG, { toastId: PROVISIONAL_FILE_VIEW_ERROR_TOAST_ID });
        if (requestId !== requestRef.current) return;
        // Settle even on failure, so loading cannot pin.
        setHeld({ key: requestKey, data: result.success ? result.data : {} });
    }, [active, docId, orgId, mis, nature, requestKey]);

    useEffect(() => {
        if (!active) {
            requestRef.current += 1;
            return;
        }
        fetchTableData();
    }, [active, fetchTableData]);

    return { tableData, loading, refetch: fetchTableData };
}
